// Building/structure voxel prefabs — adapted from adventure-craft.
import type { BlockVoxel, StructurePrefab } from './types.js';

function box(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
  type?: string,
): BlockVoxel[] {
  const out: BlockVoxel[] = [];
  for (let x = sx; x < sx + w; x++)
    for (let y = sy; y < sy + h; y++)
      for (let z = sz; z < sz + d; z++) out.push({ x, y, z, color, ...(type ? { type } : {}) });
  return out;
}

function hollowBox(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
  type?: string,
): BlockVoxel[] {
  const out: BlockVoxel[] = [];
  for (let x = sx; x < sx + w; x++)
    for (let y = sy; y < sy + h; y++)
      for (let z = sz; z < sz + d; z++)
        if (
          x === sx ||
          x === sx + w - 1 ||
          y === sy ||
          y === sy + h - 1 ||
          z === sz ||
          z === sz + d - 1
        )
          out.push({ x, y, z, color, ...(type ? { type } : {}) });
  return out;
}

const house: StructurePrefab = {
  name: 'Wooden House',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, '#8b4513', 'plank'),
    ...box(-2, 1, -2, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 1, 2, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 1, -1, 1, 3, 3, '#deb887', 'plank'),
    ...box(2, 1, -1, 1, 3, 3, '#deb887', 'plank'),
    { x: 0, y: 1, z: 2, color: '#654321', type: 'wood' },
    { x: 0, y: 2, z: 2, color: '#654321', type: 'wood' },
    { x: -1, y: 2, z: 2, color: '#87ceeb', type: 'glass' },
    { x: 1, y: 2, z: 2, color: '#87ceeb', type: 'glass' },
    ...box(-3, 4, -3, 7, 1, 7, '#8b0000', 'brick'),
    ...box(-2, 5, -2, 5, 1, 5, '#8b0000', 'brick'),
    ...box(-1, 6, -1, 3, 1, 3, '#8b0000', 'brick'),
    { x: 0, y: 7, z: 0, color: '#8b0000', type: 'brick' },
  ],
};

const tower: StructurePrefab = {
  name: 'Stone Tower',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#808080', 'stone'),
    ...hollowBox(-3, 1, -3, 7, 10, 7, '#909090', 'stone'),
    ...box(-1, 1, -4, 3, 3, 1, '#808080', 'stone'),
    ...box(-4, 3, -1, 1, 2, 3, '#606060', 'stone'),
    ...hollowBox(-2, 11, -2, 5, 2, 5, '#808080', 'stone'),
    ...box(-2, 13, -2, 5, 1, 5, '#606060', 'stone'),
    { x: 0, y: 14, z: 0, color: '#ff4444', type: 'stone' },
  ],
};

const tavern: StructurePrefab = {
  name: 'Tavern',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b4513', 'plank'),
    ...hollowBox(-3, 1, -3, 7, 4, 7, '#deb887', 'plank'),
    ...box(-2, 1, 3, 5, 3, 1, '#deb887', 'plank'),
    { x: 0, y: 1, z: 3, color: '#654321', type: 'wood' },
    { x: 0, y: 2, z: 3, color: '#654321', type: 'wood' },
    ...box(-1, 2, 3, 3, 1, 1, '#87ceeb', 'glass'),
    ...box(-1, 2, -4, 1, 2, 1, '#87ceeb', 'glass'),
    ...box(-4, 3, -1, 1, 1, 3, '#87ceeb', 'glass'),
    ...box(-3, 5, -3, 7, 1, 7, '#8b0000', 'brick'),
    ...box(-1, 6, -1, 3, 1, 3, '#8b0000', 'brick'),
    { x: 3, y: 2, z: -1, color: '#ffd700', type: 'wood' },
  ],
};

const smallCastle: StructurePrefab = {
  name: 'Small Castle',
  blocks: [
    ...box(-5, 0, -5, 11, 1, 11, '#808080', 'stone'),
    ...hollowBox(-5, 1, -5, 11, 6, 11, '#909090', 'stone'),
    ...box(-5, 1, 5, 11, 4, 1, '#787878', 'stone'),
    ...box(-5, 1, -6, 11, 4, 1, '#787878', 'stone'),
    ...box(-6, 1, -5, 1, 4, 11, '#787878', 'stone'),
    ...box(5, 1, -5, 1, 4, 11, '#787878', 'stone'),
    ...box(-6, 0, -6, 2, 8, 2, '#808080', 'stone'),
    ...box(4, 0, -6, 2, 8, 2, '#808080', 'stone'),
    ...box(-6, 0, 4, 2, 8, 2, '#808080', 'stone'),
    ...box(4, 0, 4, 2, 8, 2, '#808080', 'stone'),
    ...box(-2, 7, -2, 5, 2, 5, '#707070', 'stone'),
    ...box(-1, 9, -1, 3, 1, 3, '#606060', 'stone'),
    { x: 0, y: 10, z: 0, color: '#ffd700', type: 'stone' },
  ],
};

const temple: StructurePrefab = {
  name: 'Temple',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#e8e8e8', 'stone'),
    ...hollowBox(-4, 1, -4, 9, 6, 9, '#f5f5dc', 'stone'),
    ...box(-3, 1, -5, 7, 1, 1, '#d4d4aa', 'stone'),
    ...box(-5, 1, -3, 1, 5, 7, '#d4d4aa', 'stone'),
    ...box(4, 1, -3, 1, 5, 7, '#d4d4aa', 'stone'),
    ...box(-4, 7, -4, 9, 1, 9, '#ffd700', 'stone'),
    ...box(-2, 8, -2, 5, 1, 5, '#ffcc00', 'stone'),
    ...box(-1, 9, -1, 3, 2, 3, '#ffaa00', 'stone'),
    { x: 0, y: 11, z: 0, color: '#ffffff', type: 'stone' },
  ],
};

const church: StructurePrefab = {
  name: 'Church',
  blocks: [
    ...box(-3, 0, -5, 7, 1, 11, '#808080', 'stone'),
    ...hollowBox(-3, 1, -5, 7, 5, 11, '#b0b0b0', 'stone'),
    ...box(-1, 1, -6, 3, 3, 1, '#707070', 'stone'),
    ...box(-2, 6, -4, 5, 1, 9, '#808080', 'stone'),
    ...box(-1, 7, -3, 3, 1, 7, '#909090', 'stone'),
    ...box(-1, 8, -2, 3, 1, 5, '#a0a0a0', 'stone'),
    ...box(0, 9, -1, 2, 3, 3, '#b0b0b0', 'stone'),
    { x: 1, y: 12, z: 0, color: '#ffd700', type: 'stone' },
    { x: -2, y: 2, z: 5, color: '#87ceeb', type: 'glass' },
    { x: 2, y: 2, z: 5, color: '#87ceeb', type: 'glass' },
    { x: -2, y: 3, z: 5, color: '#87ceeb', type: 'glass' },
    { x: 2, y: 3, z: 5, color: '#87ceeb', type: 'glass' },
  ],
};

const fortress: StructurePrefab = {
  name: 'Fortress',
  blocks: [
    ...box(-6, 0, -6, 13, 1, 13, '#505050', 'stone'),
    ...hollowBox(-6, 1, -6, 13, 8, 13, '#686868', 'stone'),
    ...box(-7, 0, -7, 2, 12, 2, '#585858', 'stone'),
    ...box(6, 0, -7, 2, 12, 2, '#585858', 'stone'),
    ...box(-7, 0, 6, 2, 12, 2, '#585858', 'stone'),
    ...box(6, 0, 6, 2, 12, 2, '#585858', 'stone'),
    ...box(-7, 12, -7, 3, 1, 3, '#484848', 'stone'),
    ...box(5, 12, -7, 3, 1, 3, '#484848', 'stone'),
    ...box(-7, 12, 5, 3, 1, 3, '#484848', 'stone'),
    ...box(5, 12, 5, 3, 1, 3, '#484848', 'stone'),
    ...box(-3, 9, -3, 7, 2, 7, '#606060', 'stone'),
    ...box(-2, 11, -2, 5, 1, 5, '#585858', 'stone'),
  ],
};

const workshop: StructurePrefab = {
  name: 'Workshop',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b4513', 'plank'),
    ...box(-3, 1, -3, 7, 1, 7, '#a0522d', 'plank'),
    ...box(-3, 2, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(3, 2, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(-2, 2, -3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 2, 3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 5, -3, 5, 1, 7, '#8b4513', 'plank'),
    ...box(-1, 6, -2, 3, 1, 5, '#654321', 'wood'),
  ],
};

const market: StructurePrefab = {
  name: 'Market',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#c0c0c0', 'stone'),
    ...box(-4, 1, -4, 1, 4, 9, '#deb887', 'plank'),
    ...box(4, 1, -4, 1, 4, 9, '#deb887', 'plank'),
    ...box(-3, 1, -4, 7, 4, 1, '#deb887', 'plank'),
    ...box(-3, 1, 4, 7, 4, 1, '#deb887', 'plank'),
    ...box(-3, 5, -4, 7, 1, 9, '#8b4513', 'wood'),
    { x: -1, y: 2, z: -3, color: '#ffd700', type: 'wood' },
    { x: 1, y: 2, z: -3, color: '#ffd700', type: 'wood' },
    { x: -1, y: 2, z: 3, color: '#ffd700', type: 'wood' },
    { x: 1, y: 2, z: 3, color: '#ffd700', type: 'wood' },
  ],
};

const farm: StructurePrefab = {
  name: 'Farm',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b4513', 'wood'),
    ...box(-3, 1, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(3, 1, -3, 1, 3, 7, '#deb887', 'plank'),
    ...box(-2, 1, -3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 1, 3, 5, 3, 1, '#deb887', 'plank'),
    ...box(-2, 4, -3, 5, 1, 7, '#a0522d', 'wood'),
    ...box(-1, 5, -2, 3, 1, 5, '#8b4513', 'wood'),
    { x: 0, y: 6, z: 0, color: '#cd853f', type: 'wood' },
    { x: -2, y: 1, z: 1, color: '#ffd700', type: 'wood' },
    { x: 2, y: 1, z: 1, color: '#ffd700', type: 'wood' },
  ],
};

// Mine prefabs — stone entrance with colored accents.
const ironMine: StructurePrefab = {
  name: 'Iron Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#808080', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#787878', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#686868', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#585858', 'stone'),
    { x: 0, y: 6, z: 0, color: '#cc6644', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#cc6644', type: 'stone' },
    { x: -2, y: 1, z: -2, color: '#cc6644', type: 'stone' },
  ],
};

const silverMine: StructurePrefab = {
  name: 'Silver Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#909090', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#888888', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#787878', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#686868', 'stone'),
    { x: 0, y: 6, z: 0, color: '#c0c0e0', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#c0c0e0', type: 'stone' },
  ],
};

const goldMine: StructurePrefab = {
  name: 'Gold Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#a08850', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#908040', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#807030', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#706020', 'stone'),
    { x: 0, y: 6, z: 0, color: '#ffd700', type: 'stone' },
    { x: -2, y: 1, z: -2, color: '#ffd700', type: 'stone' },
    { x: 2, y: 2, z: -1, color: '#ffd700', type: 'stone' },
  ],
};

const crystalMine: StructurePrefab = {
  name: 'Crystal Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#706080', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#807090', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#706080', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#605070', 'stone'),
    { x: 0, y: 6, z: 0, color: '#cc88ff', type: 'stone' },
    { x: -2, y: 1, z: 2, color: '#cc88ff', type: 'stone' },
    { x: 2, y: 2, z: -2, color: '#aa66ee', type: 'stone' },
  ],
};

const darkCrystalMine: StructurePrefab = {
  name: 'Dark Crystal Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#302030', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#403040', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#382838', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#302030', 'stone'),
    { x: 0, y: 6, z: 0, color: '#6600cc', type: 'stone' },
    { x: -2, y: 1, z: -2, color: '#6600cc', type: 'stone' },
    { x: 2, y: 2, z: 2, color: '#440088', type: 'stone' },
  ],
};

// Harbour — wooden pier extending into water.
const harbour: StructurePrefab = {
  name: 'Harbour',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b7355', 'plank'),
    ...box(-2, 1, -2, 5, 1, 5, '#a08060', 'plank'),
    ...box(-4, 0, -1, 2, 3, 3, '#6b4c32', 'wood'),
    ...box(3, 0, -1, 2, 3, 3, '#6b4c32', 'wood'),
    ...box(-3, 0, -5, 7, 1, 3, '#8b7355', 'plank'),
    ...box(-2, 0, -5, 3, 1, 1, '#a08060', 'plank'),
    { x: 0, y: 1, z: -4, color: '#c4a45a', type: 'wood' },
    { x: -1, y: 2, z: -4, color: '#ff4444', type: 'wood' },
    { x: 1, y: 2, z: -4, color: '#ff4444', type: 'wood' },
  ],
};

// Shipyard — larger harbour with construction frame.
const shipyard: StructurePrefab = {
  name: 'Shipyard',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#8b7355', 'plank'),
    ...box(-3, 1, -3, 7, 1, 7, '#a08060', 'plank'),
    ...box(-5, 0, -2, 2, 4, 5, '#6b4c32', 'wood'),
    ...box(4, 0, -2, 2, 4, 5, '#6b4c32', 'wood'),
    ...box(-4, 0, -6, 9, 1, 3, '#8b7355', 'plank'),
    ...box(-3, 0, -6, 7, 1, 1, '#a08060', 'plank'),
    ...box(-2, 4, -2, 5, 1, 5, '#6b4c32', 'wood'),
    { x: 0, y: 1, z: -5, color: '#c4a45a', type: 'wood' },
    { x: -1, y: 2, z: -5, color: '#ff4444', type: 'wood' },
    { x: 1, y: 2, z: -5, color: '#ff4444', type: 'wood' },
    { x: 2, y: 2, z: -5, color: '#44ff44', type: 'wood' },
  ],
};

// Dark Shrine — chaos-aligned temple variant.
const darkShrine: StructurePrefab = {
  name: 'Dark Shrine',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#1a1018', 'stone'),
    ...hollowBox(-4, 1, -4, 9, 5, 9, '#2a1828', 'stone'),
    ...box(-3, 1, -5, 7, 1, 1, '#1a1018', 'stone'),
    ...box(-5, 1, -3, 1, 4, 7, '#1a1018', 'stone'),
    ...box(4, 1, -3, 1, 4, 7, '#1a1018', 'stone'),
    ...box(-4, 6, -4, 9, 1, 9, '#331122', 'stone'),
    ...box(-2, 7, -2, 5, 1, 5, '#441133', 'stone'),
    ...box(-1, 8, -1, 3, 2, 3, '#551144', 'stone'),
    { x: 0, y: 10, z: 0, color: '#ff1144', type: 'stone' },
    { x: -3, y: 1, z: -3, color: '#ff1144', type: 'stone' },
    { x: 3, y: 1, z: 3, color: '#ff1144', type: 'stone' },
  ],
};

// Mana Font — crystalline fountain of arcane energy.
const manaFont: StructurePrefab = {
  name: 'Mana Font',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#303050', 'stone'),
    ...box(-2, 1, -2, 5, 1, 5, '#353560', 'stone'),
    ...box(-1, 2, -1, 3, 1, 3, '#404080', 'stone'),
    { x: 0, y: 3, z: 0, color: '#5555ff', type: 'stone' },
    { x: 0, y: 4, z: 0, color: '#6666ff', type: 'stone' },
    { x: 0, y: 5, z: 0, color: '#8888ff', type: 'stone' },
    { x: 1, y: 3, z: 0, color: '#4444cc', type: 'stone' },
    { x: -1, y: 3, z: 0, color: '#4444cc', type: 'stone' },
    { x: 0, y: 3, z: 1, color: '#4444cc', type: 'stone' },
    { x: 0, y: 3, z: -1, color: '#4444cc', type: 'stone' },
    // Floating crystal shards above
    { x: 1, y: 6, z: 0, color: '#aaaaff', type: 'glass' },
    { x: -1, y: 6, z: 0, color: '#aaaaff', type: 'glass' },
    { x: 0, y: 7, z: 1, color: '#ccccff', type: 'glass' },
    { x: 0, y: 6, z: -1, color: '#aaaaff', type: 'glass' },
  ],
};

// Grand Forge — massive industrial forge with chimney stacks.
const grandForge: StructurePrefab = {
  name: 'Grand Forge',
  blocks: [
    ...box(-5, 0, -5, 11, 1, 11, '#484848', 'stone'),
    ...hollowBox(-5, 1, -5, 11, 5, 11, '#585858', 'stone'),
    // Chimney stacks
    ...box(-4, 6, -4, 2, 4, 2, '#404040', 'stone'),
    ...box(3, 6, -4, 2, 4, 2, '#404040', 'stone'),
    ...box(-4, 6, 3, 2, 4, 2, '#404040', 'stone'),
    ...box(3, 6, 3, 2, 4, 2, '#404040', 'stone'),
    // Forge glow
    { x: 0, y: 1, z: 0, color: '#ff6600', type: 'stone' },
    { x: 1, y: 1, z: 0, color: '#ff6600', type: 'stone' },
    { x: -1, y: 1, z: 0, color: '#ff6600', type: 'stone' },
    { x: 0, y: 2, z: 0, color: '#ff8800', type: 'stone' },
    { x: 1, y: 1, z: 1, color: '#ff6600', type: 'stone' },
    { x: -1, y: 1, z: -1, color: '#ff6600', type: 'stone' },
    // Chimney fire tops
    { x: -3, y: 10, z: -3, color: '#ff4400', type: 'stone' },
    { x: 4, y: 10, z: -3, color: '#ff4400', type: 'stone' },
    { x: -3, y: 10, z: 4, color: '#ff4400', type: 'stone' },
    { x: 4, y: 10, z: 4, color: '#ff4400', type: 'stone' },
    // Anvil block
    { x: 0, y: 1, z: -3, color: '#303030', type: 'stone' },
    { x: 1, y: 1, z: -3, color: '#303030', type: 'stone' },
    { x: 0, y: 2, z: -3, color: '#404040', type: 'stone' },
  ],
};

// Observatory — tall tower with a domed top.
const observatory: StructurePrefab = {
  name: 'Observatory',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#a09080', 'stone'),
    ...hollowBox(-3, 1, -2, 7, 8, 5, '#c0b0a0', 'stone'),
    // Dome
    ...box(-3, 9, -3, 7, 1, 7, '#8090b0', 'stone'),
    ...box(-2, 10, -2, 5, 1, 5, '#7080a0', 'stone'),
    ...box(-1, 11, -1, 3, 1, 3, '#607090', 'stone'),
    { x: 0, y: 12, z: 0, color: '#4488cc', type: 'glass' },
    // Telescope
    { x: 0, y: 10, z: 2, color: '#cccc88', type: 'wood' },
    { x: 0, y: 11, z: 2, color: '#dddd88', type: 'wood' },
    { x: 0, y: 11, z: 3, color: '#4488cc', type: 'glass' },
    // Windows
    { x: 3, y: 3, z: 0, color: '#4488cc', type: 'glass' },
    { x: -3, y: 3, z: 0, color: '#4488cc', type: 'glass' },
    { x: 0, y: 3, z: -2, color: '#4488cc', type: 'glass' },
    { x: 3, y: 6, z: 0, color: '#4488cc', type: 'glass' },
    { x: -3, y: 6, z: 0, color: '#4488cc', type: 'glass' },
  ],
};

// Portal of Light — golden archway with bright glow.
const portalLight: StructurePrefab = {
  name: 'Portal of Light',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#c0b090', 'stone'),
    // Arch pillars
    ...box(-3, 1, -1, 1, 6, 3, '#d4c8a0', 'stone'),
    ...box(3, 1, -1, 1, 6, 3, '#d4c8a0', 'stone'),
    // Arch top
    ...box(-2, 7, -1, 5, 1, 3, '#d4c8a0', 'stone'),
    ...box(-1, 8, -1, 3, 1, 3, '#ffd700', 'stone'),
    // Portal glow (center of arch)
    { x: 0, y: 1, z: 0, color: '#ffffff', type: 'glass' },
    { x: 0, y: 2, z: 0, color: '#ffffcc', type: 'glass' },
    { x: 0, y: 3, z: 0, color: '#ffffaa', type: 'glass' },
    { x: 0, y: 4, z: 0, color: '#ffffcc', type: 'glass' },
    { x: 0, y: 5, z: 0, color: '#ffffff', type: 'glass' },
    // Gold accents
    { x: -2, y: 1, z: 2, color: '#ffd700', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#ffd700', type: 'stone' },
    { x: -2, y: 5, z: 2, color: '#ffd700', type: 'stone' },
    { x: 2, y: 5, z: 2, color: '#ffd700', type: 'stone' },
  ],
};

// Portal of Darkness — dark archway with red/crimson glow.
const portalDark: StructurePrefab = {
  name: 'Portal of Darkness',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#201820', 'stone'),
    // Arch pillars
    ...box(-3, 1, -1, 1, 6, 3, '#301828', 'stone'),
    ...box(3, 1, -1, 1, 6, 3, '#301828', 'stone'),
    // Arch top
    ...box(-2, 7, -1, 5, 1, 3, '#301828', 'stone'),
    ...box(-1, 8, -1, 3, 1, 3, '#441133', 'stone'),
    // Portal glow (center of arch)
    { x: 0, y: 1, z: 0, color: '#440022', type: 'glass' },
    { x: 0, y: 2, z: 0, color: '#660033', type: 'glass' },
    { x: 0, y: 3, z: 0, color: '#880044', type: 'glass' },
    { x: 0, y: 4, z: 0, color: '#660033', type: 'glass' },
    { x: 0, y: 5, z: 0, color: '#440022', type: 'glass' },
    // Crimson accents
    { x: -2, y: 1, z: 2, color: '#cc1144', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#cc1144', type: 'stone' },
    { x: -2, y: 5, z: 2, color: '#cc1144', type: 'stone' },
    { x: 2, y: 5, z: 2, color: '#cc1144', type: 'stone' },
  ],
};

// Grand Shipyard — capstone naval building. Larger than shipyard with twin dry docks.
const grandShipyard: StructurePrefab = {
  name: 'Grand Shipyard',
  blocks: [
    ...box(-5, 0, -5, 11, 1, 11, '#8b7355', 'plank'),
    ...box(-4, 1, -4, 9, 1, 9, '#a08060', 'plank'),
    // Twin dry dock frames
    ...box(-6, 0, -3, 2, 5, 7, '#5a3c28', 'wood'),
    ...box(5, 0, -3, 2, 5, 7, '#5a3c28', 'wood'),
    // Cross beams
    ...box(-5, 5, -2, 11, 1, 5, '#6b4c32', 'wood'),
    // Extended pier into water
    ...box(-5, 0, -8, 11, 1, 4, '#8b7355', 'plank'),
    ...box(-3, 0, -9, 7, 1, 2, '#a08060', 'plank'),
    // Crane posts
    { x: -4, y: 1, z: -5, color: '#4a3020', type: 'wood' },
    { x: -4, y: 2, z: -5, color: '#4a3020', type: 'wood' },
    { x: -4, y: 3, z: -5, color: '#4a3020', type: 'wood' },
    { x: -4, y: 4, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 1, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 2, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 3, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 4, z: -5, color: '#4a3020', type: 'wood' },
    // Crane arms
    { x: -4, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: -3, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: -2, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: 4, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: 3, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: 2, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    // Gold markers at pier end
    { x: 0, y: 1, z: -8, color: '#ffd700', type: 'wood' },
    { x: -1, y: 2, z: -8, color: '#ffd700', type: 'wood' },
    { x: 1, y: 2, z: -8, color: '#ffd700', type: 'wood' },
    // Red + green channel markers
    { x: -2, y: 1, z: -7, color: '#ff4444', type: 'wood' },
    { x: 2, y: 1, z: -7, color: '#44ff44', type: 'wood' },
  ],
};

export const buildingPrefabs: Record<string, StructurePrefab> = {
  house,
  tower,
  tavern,
  castle: smallCastle,
  temple,
  church,
  fortress,
  workshop,
  market,
  farm,
  iron_mine: ironMine,
  silver_mine: silverMine,
  gold_mine: goldMine,
  crystal_mine: crystalMine,
  dark_crystal_mine: darkCrystalMine,
  harbour,
  shipyard,
  dark_shrine: darkShrine,
  mana_font: manaFont,
  grand_forge: grandForge,
  observatory,
  portal_light: portalLight,
  portal_dark: portalDark,
  grand_shipyard: grandShipyard,
};
